import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CommsProvider } from './context/CommsContext';
import { TrackedProgramsProvider } from './context/TrackedProgramsProvider';
import { LoginForm } from './components/shared/LoginForm';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import { LocaleLayout } from './components/shared/LocaleLayout';
import { LoadingSpinner } from './components/ui';
import { Toaster } from './components/ui/UseToast';
import { isAcceleratorCategoryVisible, CURRENT_SITE, DEFAULT_LOCALE } from './site-config';
import { hasRole } from './utils/roleUtils';
import NarrativeWrapper from './components/NarrativeWrapper';


//Home — the `home` route renders Home5; Home2–Home5 are admin-only variants
const Home2 = lazy(() => import('./pages/Home2'));
const Home3 = lazy(() => import('./pages/Home3'));
const Home4 = lazy(() => import('./pages/Home4'));
const Home5 = lazy(() => import('./pages/Home5'));
const  SubscriptionPlans = lazy(() => import('./pages/SubscriptionPlans'));
const Register = lazy(() => import('./pages/Register'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const OrgSubscriptionSuccess = lazy(() => import('./pages/OrgSubscriptionSuccess'));
const PRVProspecting = lazy(() => import('./pages/PRVProspecting'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const VideoLibrary = lazy(() => import('./pages/VideoLibrary'));

// Legal + support. These are PUBLIC on purpose: a policy a buyer cannot read
// before paying is not a disclosed policy, and a disputing cardholder's issuer
// has to be able to reach them too. Never wrap these in ProtectedRoute.
const CancellationPolicy = lazy(() => import('./pages/Legal/CancellationPolicy'));
const RefundPolicy = lazy(() => import('./pages/Legal/RefundPolicy'));
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/Legal/TermsOfUse'));
const Faq = lazy(() => import('./pages/Support/Faq'));
const Contact = lazy(() => import('./pages/Support/Contact'));

//Admin
const UsersAdmin = lazy(() => import('./pages/UsersAdmin').then(module => ({ default: module.UsersAdmin })));
const NewsletterAdmin = lazy(() => import('./pages/NewsletterAdmin').then(module => ({ default: module.NewsletterAdmin })));
const SubscriptionPlansOrg = lazy(() => import('./pages/SubscriptionPlansOrg'));
const OrgDashboard = lazy(() => import('./pages/OrgDashboard'));
const ViewAnimations = lazy(() => import('./pages/ViewAnimations'));

//Accelerators
const AIAcceleratorsTable = lazy(() => import('./pages/Accelerators/AIAcceleratorsTable'));
const AiArtistStudioAccelerators = lazy(() => import('./pages/Accelerators/AiArtistStudioAccelerators'));
const AiAutomotiveAccelerators = lazy(() => import('./pages/Accelerators/AiAutomotiveAccelerators'));
const AiBlackMenAccelerators = lazy(() => import('./pages/Accelerators/AiBlackMenAccelerators'));
const AiBlackWomenAccelerators = lazy(() => import('./pages/Accelerators/AiBlackWomenAccelerators'));
const AiChristianAccelerators = lazy(() => import('./pages/Accelerators/AiChristianAccelerators'));
const AiCulinaryAccelerators = lazy(() => import('./pages/Accelerators/AiCulinaryAccelerators'));
const AiDisabilityAccelerators = lazy(() => import('./pages/Accelerators/AiDisabilityAccelerators'));
const AiEngineeringAccelerators = lazy(() => import('./pages/Accelerators/AiEngineeringAccelerators'));
const AiEnvironmentAccelerators = lazy(() => import('./pages/Accelerators/AiEnvironmentAccelerators'));
const AiFilmStudioAccelerators = lazy(() => import('./pages/Accelerators/AiFilmStudioAccelerators'));
const AiFurnitureMakingAccelerators = lazy(() => import('./pages/Accelerators/AiFurnitureMakingAccelerators'));
const AiHotelHospitalityAccelerators = lazy(() => import('./pages/Accelerators/AiHotelHospitalityAccelerators'));
const AiIndigenousAccelerators = lazy(() => import('./pages/Accelerators/AiIndigenousAccelerators'));
const AiJewishAccelerators = lazy(() => import('./pages/Accelerators/AiJewishAccelerators'));
const AiLGBTQAccelerators = lazy(() => import('./pages/Accelerators/AiLGBTQAccelerators'));
const AiLatinoAccelerators = lazy(() => import('./pages/Accelerators/AiLatinoAccelerators'));
const AiLowIncomeAccelerators = lazy(() => import('./pages/Accelerators/AiLowIncomeAccelerators'));
const AiMedicalAccelerators = lazy(() => import('./pages/Accelerators/AiMedicalAccelerators'));
const AiMinorityAccelerators = lazy(() => import('./pages/Accelerators/AiMinorityAccelerators'));
const AiMusicStudioAccelerators = lazy(() => import('./pages/Accelerators/AiMusicStudioAccelerators'));
const AiMuslimAccelerators = lazy(() => import('./pages/Accelerators/AiMuslimAccelerators'));
const AiNextStepAccelerators = lazy(() => import('./pages/Accelerators/AiNextStepAccelerators'));
const AiPanamaAccelerators = lazy(() => import('./pages/Accelerators/AiPanamaAccelerators'));
const AiRealEstateAccelerators = lazy(() => import('./pages/Accelerators/AiRealEstateAccelerators'));
const AiRuralAccelerators = lazy(() => import('./pages/Accelerators/AiRuralAccelerators'));
const AiSTEMAccelerators = lazy(() => import('./pages/Accelerators/AiSTEMAccelerators'));
const AiSeniorAccelerators = lazy(() => import('./pages/Accelerators/AiSeniorAccelerators'));
const AiSewingFashionAccelerators = lazy(() => import('./pages/Accelerators/AiSewingFashionAccelerators'));
const AiSingleParentAccelerators = lazy(() => import('./pages/Accelerators/AiSingleParentAccelerators'));
const AiUniversityStudentAccelerators = lazy(() => import('./pages/Accelerators/AiUniversityStudentAccelerators'));
const AiVeteransAccelerators = lazy(() => import('./pages/Accelerators/AiVeteransAccelerators'));
const AiVocationalAccelerators = lazy(() => import('./pages/Accelerators/AiVocationalAccelerators'));
const AiWellnessBeautyAccelerators = lazy(() => import('./pages/Accelerators/AiWellnessBeautyAccelerators'));
const AiWomenAccelerators = lazy(() => import('./pages/Accelerators/AiWomenAccelerators'));

//Accelerator Narrative
const GoToMarketStrategy = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/GoToMarketStrategy'));
const LongDescription = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/LongDescription'));
const MarketSize = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/MarketSize'));
const OneLinePitch = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/OneLinePitch'));
const ProblemStatement = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/ProblemStatement'));
const ShortDescription = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/ShortDescription'));
const SolutionDescription = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/SolutionDescription'));
const TargetMarket = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/TargetMarket'));
const TeamDescription = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/TeamDescription'));
const ThreeYearPlan = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/ThreeYearPlan'));
const TractionSummary = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/TractionSummary'));
const UnitEconomics = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/UnitEconomics'));
const UnitEconomicsDashboard = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/UnitEconomicsDashboard'));
const UseOfFunds = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/UseOfFunds'));
const VisionStatement = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/VisionStatement'));
const WhyNow = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/WhyNow'));
const FounderIdentity = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/FounderIdentity'));
const BusinessModelCanvas = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/BusinessModelCanvas'));
const CompetitivePositioning = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/CompetitivePositioning'));
const MetricsFramework = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/MetricsFramework'));
const TeamStrategy = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/TeamStrategy'));
const FundingStrategy = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/FundingStrategy'));
const MilestoneRoadmap = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/MilestoneRoadmap'));
const BeachheadMarket = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/BeachheadMarket'));
const ProductShowcase = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/ProductShowcase'));
const FounderPresence = lazy(() => import('./pages/Accelerators/AcceleratorNarrative/FounderPresence'));

//Calendars
const AiAcceleratorCoachCommsCalendar = lazy(() => import('./pages/Accelerators/Calendars/AiAcceleratorCoachCommsCalendar'));

//Founders
const InvestorFounderSearch = lazy(() => import('./pages/Accelerators/Founders/InvestorFounderSearch'));
const MyFounderLexicon = lazy(() => import('./pages/Accelerators/Founders/MyFounderLexicon'));
const MyFounderProfile = lazy(() => import('./pages/Accelerators/Founders/MyFounderProfile'));
const MyAcceleratorTracker = lazy(() => import('./pages/Accelerators/Founders/MyAcceleratorTracker'));

//Partnership Tools
const AcceleratorPartnershipOptimizer = lazy(() => import('./pages/Accelerators/PartnershipTools/AcceleratorPartnershipOptimizer'));

//Sewing & Fashion
const MissouriStarProfitCalculator = lazy(() => import('./pages/Accelerators/AiSewingFashionAccelerators/Missouri/MissouriStarProfitCalculator'));
const MissouriStarRetreatPlanner = lazy(() => import('./pages/Accelerators/AiSewingFashionAccelerators/Missouri/MissouriStarRetreatPlanner'));
const MissouriStarTutorialFinder = lazy(() => import('./pages/Accelerators/AiSewingFashionAccelerators/Missouri/MissouriStarTutorialFinder'));
const MulqueenFindYourClass = lazy(() => import('./pages/Accelerators/AiSewingFashionAccelerators/Mulqueen/Mulqueen_FindYourClass'));
const MulqueenSewingExplorer = lazy(() => import('./pages/Accelerators/AiSewingFashionAccelerators/Mulqueen/Mulqueen_SewingExplorer'));
const ThreadworksSewingExplorer = lazy(() => import('./pages/Accelerators/AiSewingFashionAccelerators/Mulqueen/Threadworks_SewingExplorer'));
const SVPBusinessBuilder = lazy(() => import('./pages/Accelerators/AiSewingFashionAccelerators/SVP/SVP_BusinessBuilder'));
const SVPMachineFinder = lazy(() => import('./pages/Accelerators/AiSewingFashionAccelerators/SVP/SVP_MachineFinder'));

//Accelerator Tools (jsx)
const AcceleratorComparisonFramework = lazy(() => import('./pages/Accelerators/AcceleratorTools/AcceleratorComparisonFramework'));
const AcceptanceRateReality = lazy(() => import('./pages/Accelerators/AcceleratorTools/AcceptanceRateReality'));
const AlumniNetworkEstimator = lazy(() => import('./pages/Accelerators/AcceleratorTools/AlumniNetworkEstimator'));
const CohortTimingStrategy = lazy(() => import('./pages/Accelerators/AcceleratorTools/CohortTimingStrategy'));
const DeadlineCalculator = lazy(() => import('./pages/Accelerators/AcceleratorTools/DeadlineCalculator'));
const DemoDayPrepGuide = lazy(() => import('./pages/Accelerators/AcceleratorTools/DemoDayPrepGuide'));
const DueDiligenceChecklist = lazy(() => import('./pages/Accelerators/AcceleratorTools/DueDiligenceChecklist'));
const EquityDilutionVisualizer = lazy(() => import('./pages/Accelerators/AcceleratorTools/EquityDilutionVisualizer'));
const SAFEInvestmentModel = lazy(() => import('./pages/Accelerators/AcceleratorTools/SAFEInvestmentModel'));
const FounderMarketFitAssessment = lazy(() => import('./pages/Accelerators/AcceleratorTools/FounderMarketFitAssessment'));
const FounderStoryFramework = lazy(() => import('./pages/Accelerators/AcceleratorTools/FounderStoryFramework'));
const FundingLandscapeInfographics = lazy(() => import('./pages/Accelerators/AcceleratorTools/FundingLandscapeInfographics'));
const FundingMultiplierCalculator = lazy(() => import('./pages/Accelerators/AcceleratorTools/FundingMultiplierCalculator'));
const FundingReadinessAssessment = lazy(() => import('./pages/Accelerators/AcceleratorTools/FundingReadinessAssessment'));
const HiddenGemsReport = lazy(() => import('./pages/Accelerators/AcceleratorTools/HiddenGemsReport'));
const InterviewPrepGuide = lazy(() => import('./pages/Accelerators/AcceleratorTools/InterviewPrepGuide'));
const MonthlyAMATemplate = lazy(() => import('./pages/Accelerators/AcceleratorTools/MonthlyAMATemplate'));
const NetworkingROICalculator = lazy(() => import('./pages/Accelerators/AcceleratorTools/NetworkingROICalculator'));
const PitchDeckFramework = lazy(() => import('./pages/Accelerators/AcceleratorTools/PitchDeckFramework'));
const PortfolioStrategyCalculator = lazy(() => import('./pages/Accelerators/AcceleratorTools/PortfolioStrategyCalculator'));
const PostAcceptanceNegotiation = lazy(() => import('./pages/Accelerators/AcceleratorTools/PostAcceptanceNegotiation'));
const ProgramFitScorecard = lazy(() => import('./pages/Accelerators/AcceleratorTools/ProgramFitScorecard'));
const ProgramResearchMethodology = lazy(() => import('./pages/Accelerators/AcceleratorTools/ProgramResearchMethodology'));
const ReapplicationStrategyGuide = lazy(() => import('./pages/Accelerators/AcceleratorTools/ReapplicationStrategyGuide'));
const RedFlagsChecklist = lazy(() => import('./pages/Accelerators/AcceleratorTools/RedFlagsChecklist'));
const ReferenceCheckGuide = lazy(() => import('./pages/Accelerators/AcceleratorTools/ReferenceCheckGuide'));
const RejectionRecoveryFramework = lazy(() => import('./pages/Accelerators/AcceleratorTools/RejectionRecoveryFramework'));
const StageAppropriateFinder = lazy(() => import('./pages/Accelerators/AcceleratorTools/StageAppropriateFinder'));
const The100HourProblem = lazy(() => import('./pages/Accelerators/AcceleratorTools/The100HourProblem'));
const WeeklyDeadlineDigest = lazy(() => import('./pages/Accelerators/AcceleratorTools/WeeklyDeadlineDigest'));
const WhatAcceleratorsLookFor = lazy(() => import('./pages/Accelerators/AcceleratorTools/WhatAcceleratorsLookFor'));
const SalesStrategyOptimizer = lazy(() => import('./pages/Accelerators/AcceleratorTools/sales_strategy_optimizer'));

//Accelerator Tools (Value-Add Modules, tsx)
const RunwayScenarioModel = lazy(() => import('./pages/Accelerators/AcceleratorTools/RunwayScenarioModel'));
const CustomerDiscoveryTracker = lazy(() => import('./pages/Accelerators/AcceleratorTools/CustomerDiscoveryTracker'));
const InvestorPipelineTracker = lazy(() => import('./pages/Accelerators/AcceleratorTools/InvestorPipelineTracker'));
const InvestorUpdateGenerator = lazy(() => import('./pages/Accelerators/AcceleratorTools/InvestorUpdateGenerator'));
const MentorOfficeHours = lazy(() => import('./pages/Accelerators/AcceleratorTools/MentorOfficeHours'));
const EsopOptionPoolPlanner = lazy(() => import('./pages/Accelerators/AcceleratorTools/EsopOptionPoolPlanner'));
const CohortBenchmarking = lazy(() => import('./pages/Accelerators/AcceleratorTools/CohortBenchmarking'));
const DataRoomBuilder = lazy(() => import('./pages/Accelerators/AcceleratorTools/DataRoomBuilder'));

// Accelerator Budgets (per-category grant/operations budget builder)
const FilmStudioBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiFilmStudio_Budget'));
const SewingFashionBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiSewingFashion_Budget'));
const ArtistStudioBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiArtistStudio_Budget'));
const AutomotiveBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiAutomotive_Budget'));
const BlackMenBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiBlackMen_Budget'));
const BlackWomenBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiBlackWomen_Budget'));
const ChristianBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiChristian_Budget'));
const CulinaryBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiCulinary_Budget'));
const DisabilityBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiDisability_Budget'));
const EngineeringBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiEngineering_Budget'));
const EnvironmentBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiEnvironment_Budget'));
const FurnitureMakingBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiFurnitureMaking_Budget'));
const HotelHospitalityBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiHotelHospitality_Budget'));
const IndigenousBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiIndigenous_Budget'));
const JewishBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiJewish_Budget'));
const LGBTQBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiLGBTQ_Budget'));
const LatinoBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiLatino_Budget'));
const LowIncomeBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiLowIncome_Budget'));
const MedicalBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiMedical_Budget'));
const MinorityBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiMinority_Budget'));
const MusicStudioBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiMusicStudio_Budget'));
const MuslimBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiMuslim_Budget'));
const NextStepBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiNextStep_Budget'));
const PanamaBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiPanama_Budget'));
const RealEstateBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiRealEstate_Budget'));
const RuralBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiRural_Budget'));
const STEMBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiSTEM_Budget'));
const SeniorBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiSenior_Budget'));
const SingleParentBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiSingleParent_Budget'));
const UniversityStudentBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiUniversityStudent_Budget'));
const VeteransBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiVeterans_Budget'));
const VocationalBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiVocational_Budget'));
const WellnessBeautyBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiWellnessBeauty_Budget'));
const WomenBudget = lazy(() => import('./pages/Accelerators/AcceleratorBudgets/AiWomen_Budget'));


// Loading fallback component
const PageLoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);


// Main app content
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, user: currentUser } = useAuth();

  console.log('AppContent - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading);

  if (isLoading) {
    return <PageLoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<PageLoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Navigate to={`/${DEFAULT_LOCALE}/home`} replace />} />
          <Route path="/:locale" element={<LocaleLayout />}>
            {/* Logged-out landing page is the public marketing home; login has its own route. */}
            <Route index element={<Navigate to="home" replace />} />
            <Route path="login" element={<LoginForm />} />
            <Route path="register" element={<Register />} />
            <Route path="subscription-plans" element={<SubscriptionPlans />} />
            <Route path="verify-email" element={<VerifyEmail />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="org-subscription-success" element={<OrgSubscriptionSuccess />} />
            <Route path="home" element={<Home5 />} />
            <Route path="home-2" element={<ProtectedRoute requiredRoles={['admin']}><Home2 /></ProtectedRoute>} />
            <Route path="home-3" element={<ProtectedRoute requiredRoles={['admin']}><Home3 /></ProtectedRoute>} />
            <Route path="home-4" element={<ProtectedRoute requiredRoles={['admin']}><Home4 /></ProtectedRoute>} />
            <Route path="home-5" element={<ProtectedRoute requiredRoles={['admin']}><Home5 /></ProtectedRoute>} />
            {/* Public legal + support — must stay outside ProtectedRoute. */}
            <Route path="cancellation-policy" element={<CancellationPolicy />} />
            <Route path="refund-policy" element={<RefundPolicy />} />
            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            <Route path="terms-of-use" element={<TermsOfUse />} />
            <Route path="faq" element={<Faq />} />
            <Route path="contact" element={<Contact />} />
            <Route path="*" element={<LoginForm />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  console.log('User is authenticated, rendering main app');
  return (
    <CommsProvider>
      <TrackedProgramsProvider>
      <Suspense fallback={<PageLoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Navigate to={`/${DEFAULT_LOCALE}/user-dashboard`} replace />} />
          <Route path="/:locale" element={<LocaleLayout />}>
          <Route index element={<Navigate to="user-dashboard" replace />} />

          {/* Marketing home pages, matching the logged-out routes above. `home` renders Home5
              and stays unrestricted; the numbered variants are admin-only. */}
          <Route path="home" element={<Home5 />} />
          <Route path="home-2" element={<ProtectedRoute requiredRoles={['admin']}><Home2 /></ProtectedRoute>} />
          <Route path="home-3" element={<ProtectedRoute requiredRoles={['admin']}><Home3 /></ProtectedRoute>} />
          <Route path="home-4" element={<ProtectedRoute requiredRoles={['admin']}><Home4 /></ProtectedRoute>} />
          <Route path="home-5" element={<ProtectedRoute requiredRoles={['admin']}><Home5 /></ProtectedRoute>} />
          <Route
            path="user-dashboard"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']}>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="video-library"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']}>
                <VideoLibrary />
              </ProtectedRoute>
            }
          />
          <Route path="subscription-plans" element={<SubscriptionPlans />} />
          <Route path="register" element={<Register />} />
          <Route path="verify-email" element={<VerifyEmail />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="org-subscription-success" element={<OrgSubscriptionSuccess />} />
          {/* Public legal + support — must stay outside ProtectedRoute. */}
          <Route path="cancellation-policy" element={<CancellationPolicy />} />
          <Route path="refund-policy" element={<RefundPolicy />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="terms-of-use" element={<TermsOfUse />} />
          <Route path="faq" element={<Faq />} />
          <Route path="contact" element={<Contact />} />
          <Route
            path="prv-prospecting"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']}>
                <PRVProspecting />
              </ProtectedRoute>
            }
          />

          {/* ACCELERATORS section — cross-vertical table, main site only (Sidebar.tsx uses the same check) */}
          {CURRENT_SITE.siteId === 'main' && (
            <Route path="accelerators/ai-accelerators-table" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AIAcceleratorsTable /></ProtectedRoute>} />
          )}

          {/* ACCELERATORS section — filtered by site config */}
          {isAcceleratorCategoryVisible('ai-artist-studio') && (
            <Route path="accelerators/ai-artist-studio" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiArtistStudioAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-automotive') && (
            <Route path="accelerators/ai-automotive" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiAutomotiveAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-black-men') && (
            <Route path="accelerators/ai-black-men" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiBlackMenAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-black-women') && (
            <Route path="accelerators/ai-black-women" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiBlackWomenAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-christian') && (
            <Route path="accelerators/ai-christian" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiChristianAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-culinary') && (
            <Route path="accelerators/ai-culinary" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiCulinaryAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-disability') && (
            <Route path="accelerators/ai-disability" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiDisabilityAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-engineering') && (
            <Route path="accelerators/ai-engineering" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiEngineeringAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-environment') && (
            <Route path="accelerators/ai-environment" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiEnvironmentAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-film-studio') && (
            <Route path="accelerators/ai-film-studio" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiFilmStudioAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-furniture-making') && (
            <Route path="accelerators/ai-furniture-making" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiFurnitureMakingAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-hotel-hospitality') && (
            <Route path="accelerators/ai-hotel-hospitality" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiHotelHospitalityAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-indigenous') && (
            <Route path="accelerators/ai-indigenous" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiIndigenousAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-jewish') && (
            <Route path="accelerators/ai-jewish" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiJewishAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-lgbtq') && (
            <Route path="accelerators/ai-lgbtq" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiLGBTQAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-latino') && (
            <Route path="accelerators/ai-latino" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiLatinoAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-low-income') && (
            <Route path="accelerators/ai-low-income" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiLowIncomeAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-medical') && (
            <Route path="accelerators/ai-medical" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiMedicalAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-minority') && (
            <Route path="accelerators/ai-minority" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiMinorityAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-music-studio') && (
            <Route path="accelerators/ai-music-studio" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiMusicStudioAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-muslim') && (
            <Route path="accelerators/ai-muslim" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiMuslimAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-next-step') && (
            <Route path="accelerators/ai-next-step" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiNextStepAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-panama') && (
            <Route path="accelerators/ai-panama" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiPanamaAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-real-estate') && (
            <Route path="accelerators/ai-real-estate" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiRealEstateAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-rural') && (
            <Route path="accelerators/ai-rural" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiRuralAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-stem') && (
            <Route path="accelerators/ai-stem" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiSTEMAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-senior') && (
            <Route path="accelerators/ai-senior" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiSeniorAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-sewing-fashion') && (
            <Route path="accelerators/ai-sewing-fashion" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiSewingFashionAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-single-parent') && (
            <Route path="accelerators/ai-single-parent" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiSingleParentAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-university-student') && (
            <Route path="accelerators/ai-university-student" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiUniversityStudentAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-veterans') && (
            <Route path="accelerators/ai-veterans" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiVeteransAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-vocational') && (
            <Route path="accelerators/ai-vocational" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiVocationalAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-wellness-beauty') && (
            <Route path="accelerators/ai-wellness-beauty" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiWellnessBeautyAccelerators /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-women') && (
            <Route path="accelerators/ai-women" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiWomenAccelerators /></ProtectedRoute>} />
          )}

          {/* ACCELERATOR NARRATIVE section */}
          <Route path="accelerator-narrative/one-line-pitch" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="one_line_pitch">
              <NarrativeWrapper moduleId="one_line_pitch" fieldName="oneLinePitch">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <OneLinePitch initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/short-description" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="short_description">
              <NarrativeWrapper moduleId="short_description" fieldName="shortDescription">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <ShortDescription initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/long-description" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="long_description">
              <NarrativeWrapper moduleId="long_description" fieldName="longDescription">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <LongDescription initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/problem-statement" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="problem_statement">
              <NarrativeWrapper moduleId="problem_statement" fieldName="problemStatement">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <ProblemStatement initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/solution-description" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="solution_description">
              <NarrativeWrapper moduleId="solution_description" fieldName="solutionDescription">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <SolutionDescription initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/why-now" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="why_now">
              <NarrativeWrapper moduleId="why_now" fieldName="whyNow">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <WhyNow initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/target-market" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="target_market">
              <NarrativeWrapper moduleId="target_market" fieldName="targetMarket">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <TargetMarket initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/market-size" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="market_size">
              <NarrativeWrapper moduleId="market_size" fieldName="marketSize">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <MarketSize initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/go-to-market-strategy" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="go_to_market_strategy">
              <NarrativeWrapper moduleId="go_to_market_strategy" fieldName="goToMarketStrategy">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <GoToMarketStrategy initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/traction-summary" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="traction_summary">
              <NarrativeWrapper moduleId="traction_summary" fieldName="tractionSummary">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <TractionSummary initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/unit-economics" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="unit_economics">
              <NarrativeWrapper moduleId="unit_economics" fieldName="unitEconomics">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <UnitEconomics initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/unit-economics-dashboard" element={<ProtectedRoute requiredRoles={['admin', 'user']}><UnitEconomicsDashboard /></ProtectedRoute>} />
          <Route path="accelerator-narrative/team-description" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="team_description">
              <NarrativeWrapper moduleId="team_description" fieldName="teamDescription">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <TeamDescription initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/use-of-funds" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="use_of_funds">
              <NarrativeWrapper moduleId="use_of_funds" fieldName="useOfFunds">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <UseOfFunds initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/vision-statement" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="vision_statement">
              <NarrativeWrapper moduleId="vision_statement" fieldName="visionStatement">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <VisionStatement initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/three-year-plan" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="three_year_plan">
              <NarrativeWrapper moduleId="three_year_plan" fieldName="threeYearPlan">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <ThreeYearPlan initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/founder-identity" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="founder_identity">
              <NarrativeWrapper moduleId="founder_identity" fieldName="founderName">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <FounderIdentity initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/business-model-canvas" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="business_model_canvas">
              <NarrativeWrapper moduleId="business_model_canvas" fieldName="businessModel">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <BusinessModelCanvas initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/competitive-positioning" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="competitive_positioning">
              <NarrativeWrapper moduleId="competitive_positioning" fieldName="competitiveAdvantage">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <CompetitivePositioning initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/metrics-framework" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="metrics_framework">
              <NarrativeWrapper moduleId="metrics_framework" fieldName="currentMRR">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <MetricsFramework initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/team-strategy" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="team_strategy">
              <NarrativeWrapper moduleId="team_strategy" fieldName="teamSize">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <TeamStrategy initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/funding-strategy" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="funding_strategy">
              <NarrativeWrapper moduleId="funding_strategy" fieldName="fundingHistory">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <FundingStrategy initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/milestone-roadmap" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="milestone_roadmap">
              <NarrativeWrapper moduleId="milestone_roadmap" fieldName="keyMilestones">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <MilestoneRoadmap initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/beachhead-market" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="beachhead_market">
              <NarrativeWrapper moduleId="beachhead_market" fieldName="beachheadMarket">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <BeachheadMarket initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/product-showcase" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="product_showcase">
              <NarrativeWrapper moduleId="product_showcase" fieldName="productStatus">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <ProductShowcase initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />
          <Route path="accelerator-narrative/founder-presence" element={
            <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="founder_presence">
              <NarrativeWrapper moduleId="founder_presence" fieldName="companyWebsite">
                {({ initialValue, onComplete, backToLexiconButton }) => (
                  <FounderPresence initialValue={initialValue} onComplete={onComplete} backToLexiconButton={backToLexiconButton} />
                )}
              </NarrativeWrapper>
            </ProtectedRoute>
          } />

          {/* CALENDARS section */}
          <Route path="accelerators/comms-calendar" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AiAcceleratorCoachCommsCalendar /></ProtectedRoute>} />

          {/* FOUNDERS section */}
          <Route path="founders/investor-founder-search" element={<ProtectedRoute requiredRoles={['admin', 'user', 'investor']}><InvestorFounderSearch /></ProtectedRoute>} />
          <Route path="founders/my-founder-lexicon" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MyFounderLexicon /></ProtectedRoute>} />
          <Route path="founders/my-founder-profile" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MyFounderProfile /></ProtectedRoute>} />
          <Route path="founders/my-accelerator-tracker" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MyAcceleratorTracker /></ProtectedRoute>} />

          {/* PARTNERSHIP TOOLS section */}
          <Route path="accelerators/partnership-optimizer" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AcceleratorPartnershipOptimizer /></ProtectedRoute>} />

          {/* ACCELERATOR TOOLS section */}
          <Route path="accelerator-tools/accelerator-comparison-framework" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AcceleratorComparisonFramework /></ProtectedRoute>} />
          <Route path="accelerator-tools/acceptance-rate-reality" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AcceptanceRateReality /></ProtectedRoute>} />
          <Route path="accelerator-tools/alumni-network-estimator" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AlumniNetworkEstimator /></ProtectedRoute>} />
          <Route path="accelerator-tools/cohort-timing-strategy" element={<ProtectedRoute requiredRoles={['admin', 'user']}><CohortTimingStrategy /></ProtectedRoute>} />
          <Route path="accelerator-tools/deadline-calculator" element={<ProtectedRoute requiredRoles={['admin', 'user']}><DeadlineCalculator /></ProtectedRoute>} />
          <Route path="accelerator-tools/demo-day-prep-guide" element={<ProtectedRoute requiredRoles={['admin', 'user']}><DemoDayPrepGuide /></ProtectedRoute>} />
          <Route path="accelerator-tools/due-diligence-checklist" element={<ProtectedRoute requiredRoles={['admin', 'user']}><DueDiligenceChecklist /></ProtectedRoute>} />
          <Route path="accelerator-tools/equity-dilution-visualizer" element={<ProtectedRoute requiredRoles={['admin', 'user']}><EquityDilutionVisualizer /></ProtectedRoute>} />
          <Route path="accelerator-tools/safe-investment-model" element={<ProtectedRoute requiredRoles={['admin', 'user']}><SAFEInvestmentModel /></ProtectedRoute>} />
          <Route path="accelerator-tools/founder-market-fit-assessment" element={<ProtectedRoute requiredRoles={['admin', 'user']}><FounderMarketFitAssessment /></ProtectedRoute>} />
          <Route path="accelerator-tools/founder-story-framework" element={<ProtectedRoute requiredRoles={['admin', 'user']}><FounderStoryFramework /></ProtectedRoute>} />
          <Route path="accelerator-tools/funding-landscape-infographics" element={<ProtectedRoute requiredRoles={['admin', 'user']}><FundingLandscapeInfographics /></ProtectedRoute>} />
          <Route path="accelerator-tools/funding-multiplier-calculator" element={<ProtectedRoute requiredRoles={['admin', 'user']}><FundingMultiplierCalculator /></ProtectedRoute>} />
          <Route path="accelerator-tools/funding-readiness-assessment" element={<ProtectedRoute requiredRoles={['admin', 'user']}><FundingReadinessAssessment /></ProtectedRoute>} />
          <Route path="accelerator-tools/hidden-gems-report" element={<ProtectedRoute requiredRoles={['admin', 'user']}><HiddenGemsReport /></ProtectedRoute>} />
          <Route path="accelerator-tools/interview-prep-guide" element={<ProtectedRoute requiredRoles={['admin', 'user']}><InterviewPrepGuide /></ProtectedRoute>} />
          <Route path="accelerator-tools/monthly-ama-template" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MonthlyAMATemplate /></ProtectedRoute>} />
          <Route path="accelerator-tools/networking-roi-calculator" element={<ProtectedRoute requiredRoles={['admin', 'user']}><NetworkingROICalculator /></ProtectedRoute>} />
          <Route path="accelerator-tools/pitch-deck-framework" element={<ProtectedRoute requiredRoles={['admin', 'user']}><PitchDeckFramework /></ProtectedRoute>} />
          <Route path="accelerator-tools/portfolio-strategy-calculator" element={<ProtectedRoute requiredRoles={['admin', 'user']}><PortfolioStrategyCalculator /></ProtectedRoute>} />
          <Route path="accelerator-tools/post-acceptance-negotiation" element={<ProtectedRoute requiredRoles={['admin', 'user']}><PostAcceptanceNegotiation /></ProtectedRoute>} />
          <Route path="accelerator-tools/program-fit-scorecard" element={<ProtectedRoute requiredRoles={['admin', 'user']}><ProgramFitScorecard /></ProtectedRoute>} />
          <Route path="accelerator-tools/program-research-methodology" element={<ProtectedRoute requiredRoles={['admin', 'user']}><ProgramResearchMethodology /></ProtectedRoute>} />
          <Route path="accelerator-tools/reapplication-strategy-guide" element={<ProtectedRoute requiredRoles={['admin', 'user']}><ReapplicationStrategyGuide /></ProtectedRoute>} />
          <Route path="accelerator-tools/red-flags-checklist" element={<ProtectedRoute requiredRoles={['admin', 'user']}><RedFlagsChecklist /></ProtectedRoute>} />
          <Route path="accelerator-tools/reference-check-guide" element={<ProtectedRoute requiredRoles={['admin', 'user']}><ReferenceCheckGuide /></ProtectedRoute>} />
          <Route path="accelerator-tools/rejection-recovery-framework" element={<ProtectedRoute requiredRoles={['admin', 'user']}><RejectionRecoveryFramework /></ProtectedRoute>} />
          <Route path="accelerator-tools/stage-appropriate-finder" element={<ProtectedRoute requiredRoles={['admin', 'user']}><StageAppropriateFinder /></ProtectedRoute>} />
          <Route path="accelerator-tools/the-100-hour-problem" element={<ProtectedRoute requiredRoles={['admin', 'user']}><The100HourProblem /></ProtectedRoute>} />
          <Route path="accelerator-tools/weekly-deadline-digest" element={<ProtectedRoute requiredRoles={['admin', 'user']}><WeeklyDeadlineDigest /></ProtectedRoute>} />
          <Route path="accelerator-tools/what-accelerators-look-for" element={<ProtectedRoute requiredRoles={['admin', 'user']}><WhatAcceleratorsLookFor /></ProtectedRoute>} />
          <Route path="accelerator-tools/sales-strategy-optimizer" element={<ProtectedRoute requiredRoles={['admin', 'user']}><SalesStrategyOptimizer /></ProtectedRoute>} />
          <Route path="accelerator-tools/runway-scenario-model" element={<ProtectedRoute requiredRoles={['admin', 'user']}><RunwayScenarioModel /></ProtectedRoute>} />
          <Route path="accelerator-tools/customer-discovery-tracker" element={<ProtectedRoute requiredRoles={['admin', 'user']}><CustomerDiscoveryTracker /></ProtectedRoute>} />
          <Route path="accelerator-tools/investor-pipeline-tracker" element={<ProtectedRoute requiredRoles={['admin', 'user']}><InvestorPipelineTracker /></ProtectedRoute>} />
          <Route path="accelerator-tools/investor-update-generator" element={<ProtectedRoute requiredRoles={['admin', 'user']}><InvestorUpdateGenerator /></ProtectedRoute>} />
          <Route path="accelerator-tools/mentor-office-hours" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MentorOfficeHours /></ProtectedRoute>} />
          <Route path="accelerator-tools/esop-option-pool-planner" element={<ProtectedRoute requiredRoles={['admin', 'user']}><EsopOptionPoolPlanner /></ProtectedRoute>} />
          <Route path="accelerator-tools/cohort-benchmarking" element={<ProtectedRoute requiredRoles={['admin', 'user']}><CohortBenchmarking /></ProtectedRoute>} />
          <Route path="accelerator-tools/data-room-builder" element={<ProtectedRoute requiredRoles={['admin', 'user']}><DataRoomBuilder /></ProtectedRoute>} />

          {/* ACCELERATOR BUDGETS section — per-category, filtered by site config (Sidebar.tsx uses the same check) */}
          {isAcceleratorCategoryVisible('ai-film-studio') && (
            <Route path="accelerator-budgets/film-studio" element={<ProtectedRoute requiredRoles={['admin', 'user']}><FilmStudioBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-sewing-fashion') && (
            <Route path="accelerator-budgets/sewing-fashion" element={<ProtectedRoute requiredRoles={['admin', 'user']}><SewingFashionBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-artist-studio') && (
            <Route path="accelerator-budgets/artist-studio" element={<ProtectedRoute requiredRoles={['admin', 'user']}><ArtistStudioBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-automotive') && (
            <Route path="accelerator-budgets/automotive" element={<ProtectedRoute requiredRoles={['admin', 'user']}><AutomotiveBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-black-men') && (
            <Route path="accelerator-budgets/black-men" element={<ProtectedRoute requiredRoles={['admin', 'user']}><BlackMenBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-black-women') && (
            <Route path="accelerator-budgets/black-women" element={<ProtectedRoute requiredRoles={['admin', 'user']}><BlackWomenBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-christian') && (
            <Route path="accelerator-budgets/christian" element={<ProtectedRoute requiredRoles={['admin', 'user']}><ChristianBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-culinary') && (
            <Route path="accelerator-budgets/culinary" element={<ProtectedRoute requiredRoles={['admin', 'user']}><CulinaryBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-disability') && (
            <Route path="accelerator-budgets/disability" element={<ProtectedRoute requiredRoles={['admin', 'user']}><DisabilityBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-engineering') && (
            <Route path="accelerator-budgets/engineering" element={<ProtectedRoute requiredRoles={['admin', 'user']}><EngineeringBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-environment') && (
            <Route path="accelerator-budgets/environment" element={<ProtectedRoute requiredRoles={['admin', 'user']}><EnvironmentBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-furniture-making') && (
            <Route path="accelerator-budgets/furniture-making" element={<ProtectedRoute requiredRoles={['admin', 'user']}><FurnitureMakingBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-hotel-hospitality') && (
            <Route path="accelerator-budgets/hotel-hospitality" element={<ProtectedRoute requiredRoles={['admin', 'user']}><HotelHospitalityBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-indigenous') && (
            <Route path="accelerator-budgets/indigenous" element={<ProtectedRoute requiredRoles={['admin', 'user']}><IndigenousBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-jewish') && (
            <Route path="accelerator-budgets/jewish" element={<ProtectedRoute requiredRoles={['admin', 'user']}><JewishBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-lgbtq') && (
            <Route path="accelerator-budgets/lgbtq" element={<ProtectedRoute requiredRoles={['admin', 'user']}><LGBTQBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-latino') && (
            <Route path="accelerator-budgets/latino" element={<ProtectedRoute requiredRoles={['admin', 'user']}><LatinoBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-low-income') && (
            <Route path="accelerator-budgets/low-income" element={<ProtectedRoute requiredRoles={['admin', 'user']}><LowIncomeBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-medical') && (
            <Route path="accelerator-budgets/medical" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MedicalBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-minority') && (
            <Route path="accelerator-budgets/minority" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MinorityBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-music-studio') && (
            <Route path="accelerator-budgets/music-studio" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MusicStudioBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-muslim') && (
            <Route path="accelerator-budgets/muslim" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MuslimBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-next-step') && (
            <Route path="accelerator-budgets/next-step" element={<ProtectedRoute requiredRoles={['admin', 'user']}><NextStepBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-panama') && (
            <Route path="accelerator-budgets/panama" element={<ProtectedRoute requiredRoles={['admin', 'user']}><PanamaBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-real-estate') && (
            <Route path="accelerator-budgets/real-estate" element={<ProtectedRoute requiredRoles={['admin', 'user']}><RealEstateBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-rural') && (
            <Route path="accelerator-budgets/rural" element={<ProtectedRoute requiredRoles={['admin', 'user']}><RuralBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-stem') && (
            <Route path="accelerator-budgets/stem" element={<ProtectedRoute requiredRoles={['admin', 'user']}><STEMBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-senior') && (
            <Route path="accelerator-budgets/senior" element={<ProtectedRoute requiredRoles={['admin', 'user']}><SeniorBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-single-parent') && (
            <Route path="accelerator-budgets/single-parent" element={<ProtectedRoute requiredRoles={['admin', 'user']}><SingleParentBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-university-student') && (
            <Route path="accelerator-budgets/university-student" element={<ProtectedRoute requiredRoles={['admin', 'user']}><UniversityStudentBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-veterans') && (
            <Route path="accelerator-budgets/veterans" element={<ProtectedRoute requiredRoles={['admin', 'user']}><VeteransBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-vocational') && (
            <Route path="accelerator-budgets/vocational" element={<ProtectedRoute requiredRoles={['admin', 'user']}><VocationalBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-wellness-beauty') && (
            <Route path="accelerator-budgets/wellness-beauty" element={<ProtectedRoute requiredRoles={['admin', 'user']}><WellnessBeautyBudget /></ProtectedRoute>} />
          )}
          {isAcceleratorCategoryVisible('ai-women') && (
            <Route path="accelerator-budgets/women" element={<ProtectedRoute requiredRoles={['admin', 'user']}><WomenBudget /></ProtectedRoute>} />
          )}

          {/* SEWING & FASHION section — admins on the sewing site only (Sidebar.tsx uses the same check) */}
          {hasRole(currentUser, 'admin') && CURRENT_SITE.siteId === 'sewing' && (
            <>
              <Route path="sewing-fashion/missouri-star-profit-calculator" element={<ProtectedRoute requiredRoles={['admin']}><MissouriStarProfitCalculator /></ProtectedRoute>} />
              <Route path="sewing-fashion/missouri-star-retreat-planner" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MissouriStarRetreatPlanner /></ProtectedRoute>} />
              <Route path="sewing-fashion/missouri-star-tutorial-finder" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MissouriStarTutorialFinder /></ProtectedRoute>} />
              <Route path="sewing-fashion/mulqueen-find-your-class" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MulqueenFindYourClass /></ProtectedRoute>} />
              <Route path="sewing-fashion/mulqueen-sewing-explorer" element={<ProtectedRoute requiredRoles={['admin', 'user']}><MulqueenSewingExplorer /></ProtectedRoute>} />
              <Route path="sewing-fashion/threadworks-sewing-explorer" element={<ProtectedRoute requiredRoles={['admin', 'user']}><ThreadworksSewingExplorer /></ProtectedRoute>} />
              <Route path="sewing-fashion/svp-business-builder" element={<ProtectedRoute requiredRoles={['admin', 'user']}><SVPBusinessBuilder /></ProtectedRoute>} />
              <Route path="sewing-fashion/svp-machine-finder" element={<ProtectedRoute requiredRoles={['admin', 'user']}><SVPMachineFinder /></ProtectedRoute>} />
            </>
          )}

          {/* ADMINISTRATION section - Admin only */}
          <Route
            path="users-admin"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <UsersAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="newsletter-admin"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <NewsletterAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="org-payment-links"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredOrgRole="admin">
                <SubscriptionPlansOrg />
              </ProtectedRoute>
            }
          />
          <Route
            path="org-dashboard"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredOrgRole="admin">
                <OrgDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="view-animations"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <ViewAnimations />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="user-dashboard" replace />} />
          </Route>
        </Routes>
      </Suspense>
      </TrackedProgramsProvider>
    </CommsProvider>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <div className="min-h-screen bg-gray-50">
          <AppContent />
          <Toaster />
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
